export const behavioralFinanceCourse = {
  id: "behavioral-finance-2024",
  title: "Behavioral Finance: Complete Professional Course",
  shortDescription: "Master behavioral finance principles, cognitive biases, and decision-making psychology in financial markets and investment management.",
  description: "Comprehensive course covering behavioral biases, market psychology, investor behavior, and applications of behavioral insights in finance and investment management.",
  category: "Finance",
  industryCategory: "Behavioral Finance",
  difficulty: "intermediate",
  duration: 16,
  estimatedWeeks: 8,
  instructorName: "Workazon Platform",
  instructorId: "platform",
  instructorBio: "Expert-curated content from behavioral finance researchers and practitioners",
  thumbnailUrl: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400",
  price: 0,
  isPublic: true,
  status: "published",
  template: "comprehensive",
  version: "1.0",
  createdBy: "platform",
  createdAt: new Date(),
  lastModified: new Date(),
  updatedAt: new Date(),

  learningObjectives: [
    "Understand cognitive biases and their impact on financial decisions",
    "Analyze market anomalies through behavioral finance lens",
    "Apply behavioral insights to improve investment processes",
    "Evaluate investor psychology and sentiment indicators",
    "Design choice architecture to improve financial outcomes",
    "Understand behavioral portfolio theory and mental accounting",
    "Analyze behavioral factors in corporate finance decisions",
    "Implement behavioral risk management strategies"
  ],

  prerequisites: [
    "Basic Finance Knowledge",
    "Statistics Fundamentals",
    "Investment Basics"
  ],

  skills: [
    "Behavioral Analysis",
    "Bias Recognition",
    "Decision Architecture",
    "Market Psychology",
    "Investor Profiling",
    "Sentiment Analysis",
    "Choice Design",
    "Behavioral Risk Management"
  ],

  marketData: {
    averageSalary: 95000,
    salaryRange: {
      min: 70000,
      max: 150000
    },
    jobDemand: 4.2,
    marketTrend: "growing",
    careerPaths: [
      "Behavioral Analyst",
      "Investment Strategist",
      "Risk Manager",
      "Financial Advisor",
      "Portfolio Manager",
      "Research Analyst"
    ],
    relatedSkills: [
      "Psychology",
      "Data Analysis",
      "Research Methods",
      "Statistical Analysis",
      "Investment Analysis",
      "Risk Assessment"
    ]
  },

  enrollmentCount: 0,
  completionRate: 0,
  averageRating: 0,
  reviewCount: 0,

  modules: [
    {
      id: "behavioral-finance-foundations",
      title: "Behavioral Finance Foundations",
      description: "Introduction to behavioral finance theory and cognitive psychology",
      order: 1,
      estimatedHours: 4.0,
      content: [
        {
          id: "behavioral-finance-intro",
          title: "Introduction to Behavioral Finance",
          description: "Foundations of behavioral finance and departure from rational markets",
          type: "video",
          duration: 35
        },
        {
          id: "cognitive-psychology-foundations",
          title: "Cognitive Psychology and Decision Making",
          description: "Psychological foundations of financial decision making",
          type: "text",
          duration: 50,
          textContent: `# Cognitive Psychology and Decision Making

## Foundations of Behavioral Finance

### Traditional Finance vs. Behavioral Finance

**Efficient Market Hypothesis (EMH)**
- Markets are informationally efficient
- Prices reflect all available information
- Investors are rational and process information correctly
- Arbitrage eliminates pricing anomalies quickly

**Behavioral Finance Challenges**
- Systematic deviations from rationality
- Persistent market anomalies
- Limited arbitrage in practice
- Psychological factors influence decisions

**Three Pillars of Behavioral Finance**
1. **Bounded Rationality**: Cognitive limitations in processing information
2. **Non-Standard Preferences**: Preferences that violate expected utility theory
3. **Limited Self-Control**: Difficulty implementing optimal decisions

### Cognitive Psychology Foundations

**Dual-Process Theory**
- **System 1 (Fast Thinking)**: Automatic, intuitive, emotional
- **System 2 (Slow Thinking)**: Deliberate, analytical, effortful
- Most financial decisions involve System 1 processing
- System 2 required for complex financial analysis

**Bounded Rationality**
- **Information Processing Limits**: Cannot process all available information
- **Time Constraints**: Decisions made under pressure
- **Cognitive Capacity**: Limited attention and memory
- **Satisficing vs. Optimizing**: Good enough rather than perfect decisions

## Prospect Theory

### Value Function Characteristics

**Reference Point Dependence**
- Outcomes evaluated relative to reference point (status quo)
- Gains and losses matter more than absolute wealth levels
- Reference points can be manipulated and are context-dependent

**Loss Aversion**
- Losses loom larger than equivalent gains
- Loss aversion coefficient typically 2.0-2.5
- Stronger emotional response to losses
- "Nothing ventured, nothing lost" mentality

**Diminishing Sensitivity**
- Decreasing marginal utility for gains
- Decreasing marginal disutility for losses
- S-shaped value function
- Risk aversion in gains, risk seeking in losses

### Decision Weights and Probability

**Probability Weighting Function**
- Overweight small probabilities
- Underweight moderate to high probabilities
- Creates fourfold pattern of risk preferences
- Explains popularity of insurance and gambling

**Fourfold Pattern**
- **High probability gains**: Risk averse (certainty effect)
- **Low probability gains**: Risk seeking (lottery effect)
- **High probability losses**: Risk seeking (sure loss aversion)
- **Low probability losses**: Risk averse (insurance effect)

## Cognitive Biases in Finance

### Availability Heuristic

**Definition and Mechanism**
- Judge probability by ease of recall
- Recent and vivid events seem more likely
- Media coverage influences perception
- Personal experience overweighted

**Financial Applications**
- Overreaction to recent market events
- Neglect of base rates in investment decisions
- Overestimation of dramatic but rare events
- Home bias in investment portfolios

### Representativeness Heuristic

**Pattern Recognition Bias**
- Judge similarity to stereotypes or patterns
- Ignore base rates and sample sizes
- See patterns in random sequences
- Expect reversals after streaks

**Financial Manifestations**
- **Hot Hand Fallacy**: Expecting streaks to continue
- **Gambler's Fallacy**: Expecting reversals after streaks
- **Small Sample Bias**: Overconfidence in limited data
- **Regression to Mean Neglect**: Ignoring natural reversion

### Anchoring and Adjustment

**Anchoring Process**
- Initial value serves as starting point
- Insufficient adjustment from anchor
- Anchors can be completely irrelevant
- Affects numerical estimates and valuations

**Financial Impact**
- Stock prices as anchors for valuation
- Recent highs/lows influence expectations
- Analyst forecasts influenced by current prices
- Negotiation outcomes affected by initial offers

## Emotional Influences on Decision Making

### Fear and Greed in Markets

**Fear-Based Decisions**
- Flight to safety during uncertainty
- Panic selling during market downturns
- Excessive risk aversion after losses
- Paralysis in decision making

**Greed-Driven Behavior**
- Chasing performance and hot investments
- Excessive risk taking in bull markets
- FOMO (Fear of Missing Out)
- Neglect of downside risks

### Mood and Market Performance

**Affect Infusion Model**
- Current mood influences judgments
- Positive mood increases optimism
- Negative mood increases pessimism
- Irrelevant mood affects financial decisions

**Weather and Market Returns**
- Sunshine associated with higher returns
- Seasonal affective patterns in trading
- Cultural and regional mood effects
- Sports outcomes affect local market performance

## Mental Accounting

### Account Categorization

**Mental Budget Categories**
- Current income vs. windfall gains
- Retirement savings vs. vacation funds
- Safe vs. risky investment accounts
- Different rules for different "accounts"

**Violations of Fungibility**
- Money treated differently based on source
- Separate mental budgets for different goals
- Reluctance to move money between accounts
- Non-optimal allocation across accounts

### Applications in Personal Finance

**Consumption Decisions**
- Spending patterns based on income source
- Credit card vs. cash spending differences
- Windfall spending vs. regular income
- Gift money treated differently

**Investment Behavior**
- Layered portfolio construction
- Different risk tolerance by account
- Asset location inefficiencies
- Reluctance to rebalance across accounts

## Behavioral Portfolio Theory

### Security Selection vs. Asset Allocation

**Bottom-Up Portfolio Construction**
- Focus on individual security selection
- Neglect of correlation and portfolio effects
- Underdiversification and concentration
- Stock picking overconfidence

**Behavioral Asset Allocation**
- Mental accounting in portfolio layers
- Different risk preferences by goal
- Conservative and aggressive sub-portfolios
- Inefficient overall allocation

### Behavioral Biases in Portfolio Management

**Familiarity Bias**
- Preference for familiar investments
- Home country and company stock bias
- Local investment preferences
- Concentration in familiar sectors

**Naive Diversification**
- Equal weighting regardless of risk/return
- 1/n heuristic in allocation decisions
- Diversification for its own sake
- Ignoring correlation and optimization

## Behavioral Decision Architecture

### Choice Architecture Principles

**Default Options**
- Automatic enrollment in retirement plans
- Default investment allocations
- Opt-out vs. opt-in framing
- Status quo bias exploitation

**Framing Effects**
- Presentation affects choices
- Gain vs. loss framing
- Percentage vs. absolute numbers
- Visual presentation impact

### Nudging in Financial Decisions

**Save More Tomorrow**
- Commitment to future savings increases
- Automatic escalation with pay raises
- Reduces present bias in savings decisions
- Leverages inertia for positive outcomes

**Simplification and Defaults**
- Reduce choice complexity
- Set beneficial defaults
- Provide clear recommendations
- Use social proof and peer comparisons

Understanding the psychological foundations of financial decision making is crucial for recognizing biases, improving investment processes, and designing better financial products and advice.`
        },
        {
          id: "bias-identification-tool",
          title: "Behavioral Bias Identification Workshop",
          description: "Learn to identify and analyze behavioral biases in financial decisions",
          type: "finance-interactive",
          duration: 40,
          interactiveData: {
            exerciseType: "bias-identification",
            instructions: "Analyze different financial decision scenarios to identify underlying behavioral biases and their potential impact on outcomes.",
            scenario: {
              scenarios: [
                {
                  id: "scenario1",
                  description: "An investor holds onto losing stocks while selling winning stocks",
                  options: ["Loss Aversion", "Anchoring", "Overconfidence", "Herding"]
                },
                {
                  id: "scenario2", 
                  description: "Portfolio manager expects recent outperformance to continue",
                  options: ["Hot Hand Fallacy", "Confirmation Bias", "Availability Heuristic", "Representativeness"]
                },
                {
                  id: "scenario3",
                  description: "Investor heavily weights home country stocks despite global opportunities",
                  options: ["Home Bias", "Familiarity Bias", "Availability Heuristic", "Mental Accounting"]
                }
              ]
            },
            hints: [
              "Consider how emotions might influence the decision",
              "Think about information processing limitations",
              "Look for patterns of systematic deviation from rationality",
              "Consider reference point dependencies"
            ]
          }
        }
      ],
      quiz: {
        id: "behavioral-foundations-quiz",
        title: "Behavioral Finance Foundations Quiz",
        description: "Test your understanding of behavioral finance theory",
        timeLimit: 20,
        passingScore: 75,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "According to Prospect Theory, how do people typically evaluate outcomes?",
            options: [
              "Based on absolute wealth levels",
              "Relative to a reference point",
              "Using expected utility theory",
              "Through rational analysis only"
            ],
            correctAnswer: 1,
            explanation: "Prospect Theory shows that people evaluate outcomes relative to a reference point (usually the status quo) rather than absolute wealth levels."
          },
          {
            id: "q2",
            type: "multiple-choice",
            question: "What is loss aversion?",
            options: [
              "The tendency to avoid all risky investments",
              "Losses hurt more than equivalent gains feel good",
              "The preference for guaranteed returns",
              "Fear of market volatility"
            ],
            correctAnswer: 1,
            explanation: "Loss aversion is the tendency for losses to have a greater psychological impact than equivalent gains, typically by a factor of 2-2.5."
          },
          {
            id: "q3",
            type: "multiple-choice",
            question: "What does bounded rationality suggest about human decision making?",
            options: [
              "Humans are perfectly rational",
              "Emotions never affect decisions",
              "Cognitive limitations affect decision quality",
              "All decisions are random"
            ],
            correctAnswer: 2,
            explanation: "Bounded rationality recognizes that humans have cognitive limitations in processing information, leading to systematic deviations from perfect rationality."
          }
        ]
      }
    },

    {
      id: "market-anomalies",
      title: "Market Anomalies and Investor Behavior",
      description: "Behavioral explanations for market anomalies and investor patterns",
      order: 2,
      estimatedHours: 4.5,
      content: [
        {
          id: "market-anomalies-overview",
          title: "Behavioral Market Anomalies",
          description: "How behavioral biases create persistent market anomalies",
          type: "video",
          duration: 40
        },
        {
          id: "momentum-reversal-patterns",
          title: "Momentum and Reversal Patterns",
          description: "Behavioral explanations for price momentum and mean reversion",
          type: "text",
          duration: 45,
          textContent: `# Momentum and Reversal Patterns

## Price Momentum Effects

### Short-term Momentum

**Empirical Evidence**
- Stocks with high returns over 3-12 months continue outperforming
- Cross-sectional momentum across different asset classes
- Momentum profits significant after transaction costs
- Persistent across different time periods and markets

**Behavioral Explanations**
- **Underreaction to Information**: Investors slow to incorporate news
- **Anchoring and Adjustment**: Insufficient adjustment from prior beliefs
- **Confirmation Bias**: Seeking information confirming existing views
- **Herding Behavior**: Following other investors' actions

### Long-term Reversal

**Mean Reversion Evidence**
- Long-term losers outperform long-term winners over 3-5 years
- Contrarian strategies profitable over longer horizons
- Stronger effect for smaller, less followed stocks
- Related to firm fundamentals and earnings reversals

**Psychological Drivers**
- **Overreaction to Information**: Excessive response to news
- **Representativeness Heuristic**: Extrapolating past performance
- **Availability Bias**: Overweighting recent vivid events
- **Social Proof**: Following crowd in extreme market moves

## Value Premium Anomaly

### Value vs. Growth Performance

**Historical Evidence**
- Value stocks (high book-to-market) outperform growth stocks
- Value premium exists across markets and time periods
- Stronger in smaller capitalization stocks
- Cyclical patterns in value premium

**Behavioral Explanations**
- **Extrapolation Bias**: Overextrapolating past growth
- **Representativeness**: Growth stocks seen as "good companies"
- **Overconfidence**: Overestimating ability to pick winners
- **Glamour vs. Value**: Preference for exciting growth stories

### Contrarian Investment Strategies

**Implementation Approaches**
- **Fundamental Indicators**: P/E, P/B, P/S, EV/EBITDA ratios
- **Price-based Signals**: Past performance reversals
- **Quality Considerations**: Financial strength and stability
- **Timing Factors**: Economic cycles and market conditions

**Behavioral Foundations**
- Exploitation of systematic mispricing
- Requires patience and discipline
- Goes against natural psychological tendencies
- Benefits from market inefficiencies

## Size Effect Anomaly

### Small Cap Premium

**Historical Performance**
- Small capitalization stocks outperform large caps
- Effect stronger in January (January effect)
- More pronounced in value stocks
- Varies significantly over time

**Behavioral and Structural Factors**
- **Neglected Firm Effect**: Less analyst coverage
- **Information Processing**: Limits on small cap research
- **Liquidity Considerations**: Higher transaction costs
- **Risk Preferences**: Behavioral risk factors

### Seasonal Patterns

**January Effect**
- Abnormally high returns in January
- Stronger for small cap and value stocks
- Tax-loss selling followed by buying
- Window dressing by institutional investors

**Other Calendar Anomalies**
- **Monday Effect**: Lower returns on Mondays
- **Holiday Effects**: Pre-holiday outperformance
- **Turn-of-Month**: Strong performance around month-end
- **Earnings Announcement**: Drift following earnings

## Post-Earnings Announcement Drift

### Drift Phenomenon

**Empirical Characteristics**
- Stock prices continue moving in direction of earnings surprise
- Persists for several quarters after announcement
- Stronger for smaller, less followed companies
- Significant even after transaction costs

**Behavioral Explanations**
- **Underreaction to Earnings**: Slow incorporation of information
- **Anchoring on Prior Expectations**: Insufficient belief updating
- **Limited Attention**: Investors miss earnings implications
- **Conservatism Bias**: Reluctance to change opinions

### Market Efficiency Implications

**Information Processing**
- Markets not immediately efficient in processing earnings
- Professional analysts also subject to biases
- Information diffusion takes time
- Complexity of earnings affects reaction speed

## IPO Anomalies

### Initial Underpricing

**First-Day Returns**
- IPOs typically close above offering price on first day
- Average underpricing varies by market conditions
- Hot issue markets show higher underpricing
- Leaves money on table for issuers

**Behavioral Factors**
- **Winner's Curse**: Informed vs. uninformed investors
- **Bandwagon Effect**: Following others into hot issues
- **Overoptimism**: Excessive enthusiasm for new issues
- **Anchoring**: Offering price as reference point

### Long-term Underperformance

**Post-IPO Performance**
- IPOs underperform market over 3-5 years after going public
- Effect strongest for growth and technology companies
- Related to initial valuation levels
- Varies with market timing and conditions

**Psychological Explanations**
- **Overreaction to Growth Stories**: Excessive optimism
- **Representativeness**: New companies seen as next big thing
- **Media Attention**: Publicity creates overvaluation
- **FOMO**: Fear of missing out on hot investments

## Closed-End Fund Puzzle

### Discount to NAV

**Puzzle Description**
- Closed-end funds often trade below net asset value
- Discounts vary over time and across funds
- New funds typically trade at premium initially
- Discounts can persist for long periods

**Behavioral Explanations**
- **Noise Trader Theory**: Uninformed investor sentiment
- **Investor Sentiment Cycles**: Optimism and pessimism waves
- **Liquidity Preferences**: Preference for open-end structure
- **Agency Costs**: Concerns about management fees

### Sentiment Indicators

**Market Sentiment Measures**
- Closed-end fund discounts as sentiment indicator
- IPO volume and initial returns
- Mutual fund flows and cash levels
- Insider trading patterns

**Predictive Power**
- Sentiment measures predict future returns
- Contrarian indicators for market timing
- Useful for asset allocation decisions
- Complement fundamental analysis

## Limits to Arbitrage

### Why Anomalies Persist

**Practical Constraints**
- **Capital Requirements**: Limited arbitrage capital
- **Risk Factors**: Fundamental and noise trader risk
- **Implementation Costs**: Transaction costs and fees
- **Institutional Constraints**: Mandates and restrictions

**Behavioral Factors**
- **Synchronization Risk**: Need for coordinated action
- **Career Concerns**: Performance relative to benchmarks
- **Loss Aversion**: Reluctance to take contrarian positions
- **Overconfidence**: Belief in own analysis vs. market

### Professional Investor Behavior

**Institutional Biases**
- **Herding**: Following peer actions
- **Benchmarking**: Closet indexing behavior
- **Career Risk**: Avoiding unusual positions
- **Short-term Focus**: Pressure for quarterly performance

**Agency Problems**
- Misalignment between manager and investor incentives
- Compensation structures encouraging conformity
- Risk management constraints limiting arbitrage
- Reputation concerns affecting decision making

## Implications for Investment Strategy

### Exploiting Behavioral Biases

**Systematic Approaches**
- **Factor Investing**: Value, momentum, quality factors
- **Contrarian Strategies**: Buying when others sell
- **Patient Capital**: Long-term perspective advantage
- **Behavioral Alpha**: Identifying systematic mistakes

### Risk Management

**Behavioral Risk Factors**
- Sentiment-driven volatility
- Crowded trade risks
- Regime change possibilities
- Model breakdown scenarios

**Portfolio Considerations**
- Diversification across behavioral factors
- Risk budgeting for behavioral strategies
- Timing and implementation considerations
- Performance measurement challenges

Understanding market anomalies through a behavioral lens provides insights into persistent market inefficiencies and opportunities for systematic investment strategies.`
        }
      ],
      quiz: {
        id: "market-anomalies-quiz",
        title: "Market Anomalies and Investor Behavior Quiz",
        description: "Test your knowledge of behavioral market anomalies",
        timeLimit: 25,
        passingScore: 75,
        attempts: 3,
        questions: [
          {
            id: "q1",
            type: "multiple-choice",
            question: "What behavioral bias best explains short-term momentum in stock prices?",
            options: [
              "Overreaction to information",
              "Underreaction to information", 
              "Loss aversion",
              "Mental accounting"
            ],
            correctAnswer: 1,
            explanation: "Short-term momentum is best explained by underreaction to information, where investors are slow to incorporate new information into stock prices."
          },
          {
            id: "q2",
            type: "multiple-choice",
            question: "What is post-earnings announcement drift?",
            options: [
              "Earnings announcements cause immediate price changes",
              "Stock prices continue moving in the direction of earnings surprise",
              "Earnings have no effect on stock prices",
              "Companies delay earnings announcements"
            ],
            correctAnswer: 1,
            explanation: "Post-earnings announcement drift refers to the tendency of stock prices to continue moving in the direction of an earnings surprise for several quarters after the announcement."
          }
        ]
      }
    }
  ],

  assignments: [
    {
      id: "behavioral-analysis-project",
      title: "Behavioral Finance Analysis Project",
      description: "Analyze a real-world financial decision or market anomaly using behavioral finance concepts",
      type: "project",
      dueWeeks: 5,
      maxScore: 100,
      instructions: "Comprehensive analysis applying behavioral finance theory to explain market phenomena or investment decisions.",
      submissionFormat: "pdf",
      resources: [
        "Behavioral finance research papers",
        "Market data and anomaly examples", 
        "Bias assessment tools"
      ]
    }
  ],

  finalExam: {
    id: "behavioral-finance-final",
    title: "Behavioral Finance Final Exam",
    description: "Comprehensive examination of behavioral finance concepts and applications",
    timeLimit: 120,
    passingScore: 80,
    attempts: 2,
    questions: [
      {
        id: "final-q1",
        type: "multiple-choice",
        question: "According to Prospect Theory, what shape is the value function?",
        options: [
          "Linear",
          "S-shaped (concave for gains, convex for losses)",
          "Exponential",
          "U-shaped"
        ],
        correctAnswer: 1,
        explanation: "The Prospect Theory value function is S-shaped: concave in the domain of gains (risk aversion) and convex in the domain of losses (risk seeking)."
      },
      {
        id: "final-q2",
        type: "multiple-choice",
        question: "What is the main idea behind the limits to arbitrage theory?",
        options: [
          "Arbitrage is always profitable",
          "Market inefficiencies persist due to practical constraints on arbitrageurs",
          "Professional investors are always rational",
          "Transaction costs are negligible"
        ],
        correctAnswer: 1,
        explanation: "Limits to arbitrage theory explains why market inefficiencies can persist despite the presence of sophisticated investors due to capital constraints, risk factors, and institutional limitations."
      }
    ]
  },

  quizzes: []
};